/**
 * Hardware Info Card Component
 *
 * Displays system hardware information including GPU, CPU, RAM, and disk.
 */

import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

import type { HardwareCapabilities } from '../../services/componentService';

interface HardwareInfoCardProps {
  hardware: HardwareCapabilities | null;
  loading: boolean;
}

export const HardwareInfoCard: React.FC<HardwareInfoCardProps> = ({ hardware, loading }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  if (loading) {
    return (
      <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 mb-6 animate-pulse">
        <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-1/3 mb-4"></div>
        <div className="space-y-2">
          <div className="h-3 bg-secondary-200 dark:bg-secondary-700 rounded w-1/2"></div>
          <div className="h-3 bg-secondary-200 dark:bg-secondary-700 rounded w-2/3"></div>
          <div className="h-3 bg-secondary-200 dark:bg-secondary-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!hardware) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 sm:p-6 mb-6">
        <p className="text-red-800 dark:text-red-200">
          {t('additionalInstallations.hardwareDetectionFailed', 'Failed to detect hardware')}
        </p>
      </div>
    );
  }

  const { gpu, system } = hardware;

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 sm:p-6 mb-6 shadow-sm">
      <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">
        {t('additionalInstallations.yourSystem', 'Your System')}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* GPU Info */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-purple-600 dark:text-purple-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
              />
            </svg>
          </div>
          <div>
            <p className="label-emphasized">{t('additionalInstallations.gpu', 'GPU')}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {gpu
                ? `${gpu.name} (${gpu.vramGb}GB VRAM)`
                : t('additionalInstallations.noGpuDetected', 'No GPU detected')}
            </p>
            {gpu?.cudaAvailable && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 mt-1">
                CUDA
              </span>
            )}
            {gpu?.metalAvailable && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 mt-1">
                Metal
              </span>
            )}
          </div>
        </div>

        {/* RAM Info */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-blue-600 dark:text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <div>
            <p className="label-emphasized">{t('additionalInstallations.ram', 'RAM')}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {system.ramTotalGb.toFixed(1)}GB {t('additionalInstallations.total', 'Total')} /{' '}
              {system.ramAvailableGb.toFixed(1)}GB{' '}
              {t('additionalInstallations.available', 'Available')}
            </p>
          </div>
        </div>

        {/* Disk Info */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
              />
            </svg>
          </div>
          <div>
            <p className="label-emphasized">{t('additionalInstallations.disk', 'Disk')}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {system.diskAvailableGb.toFixed(1)}GB {t('additionalInstallations.free', 'Free')}
            </p>
          </div>
        </div>

        {/* CPU Info */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-orange-600 dark:text-orange-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <p className="label-emphasized">{t('additionalInstallations.cpu', 'CPU')}</p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {system.cpuCores} {t('additionalInstallations.cores', 'cores')}
            </p>
          </div>
        </div>
      </div>

      {/* Details toggle */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-secondary-500 dark:text-secondary-400">
                {t('additionalInstallations.os', 'OS')}:
              </span>
              <span className="ml-2 text-secondary-900 dark:text-secondary-100 capitalize">
                {system.os}
              </span>
            </div>
            {gpu?.computeCapability && (
              <div>
                <span className="text-secondary-500 dark:text-secondary-400">
                  {t('additionalInstallations.computeCapability', 'Compute Capability')}:
                </span>
                <span className="ml-2 text-secondary-900 dark:text-secondary-100">
                  {gpu.computeCapability}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
      >
        {showDetails
          ? t('additionalInstallations.hideDetails', 'Hide Details')
          : t('additionalInstallations.viewDetails', 'View Details')}
      </button>
    </div>
  );
};

export default HardwareInfoCard;
